/**
 * Sisyphus Protocol Enforcer — standalone module for the MetaGovernor plugin.
 *
 * Reads the Sisyphus-mandatory protocol from disk, injects a condensed
 * version into the system prompt, and audits tool calls for protocol
 * violations using heuristic detection (no NLP).
 *
 * Public surface:
 * - loadProtocol(path?): Promise<string>
 * - buildSystemInjection(protocolText): string
 * - auditToolCall(toolName, args, context): ProtocolViolation[]
 */
import { readFile } from "node:fs/promises"
import { resolve } from "node:path"
import { homedir } from "node:os"
import type { ProtocolViolation } from "./types"
import { IMPLEMENTATION_TOOLS } from "./skill-priming"
import { logToFile } from "./file-logger"

// ─── Default protocol path ───────────────────────────────────────

export const DEFAULT_PROTOCOL_PATH = resolve(
  homedir(),
  ".config",
  "opencode",
  "sisyphus-mandatory",
  "sisyphus-mandatory.md",
)

// ─── loadProtocol ────────────────────────────────────────────────

// P5 (ENOENT sisyphus-mandatory): tracks whether the missing-file notice
// was already logged so an absent optional file warns exactly once per
// process instead of once per session/turn.
let missingProtocolWarned = false

/**
 * Reads the optional Sisyphus-mandatory protocol file from disk.
 *
 * The file is OPTIONAL: the real enforcement rules are embedded in
 * `buildSystemInjection()` (rules 1-9, hardcoded). The file only adds
 * extra context — its text is used solely to decide whether rule 4
 * (Post-task Oracle Verification) is emitted, via the `/\boracle\b/i`
 * test. A missing file therefore degrades gracefully to `""` (embedded
 * rules still inject) instead of throwing ENOENT into the startup log.
 *
 * - `ENOENT` (file not found) → returns `""` + one actionable `warn`
 *   per process naming the path and how to silence/provide it.
 * - Any OTHER error (EACCES, EISDIR, …) → still propagates. Real
 *   errors must never be silenced.
 */
export async function loadProtocol(path?: string): Promise<string> {
  const resolvedPath = path ?? DEFAULT_PROTOCOL_PATH
  try {
    return await readFile(resolvedPath, "utf-8")
  } catch (err: unknown) {
    const code = (err as NodeJS.ErrnoException | undefined)?.code
    if (code !== "ENOENT") throw err
    if (!missingProtocolWarned) {
      missingProtocolWarned = true
      try {
        logToFile(
          "warn",
          `protocol file not found at ${resolvedPath}; using built-in protocol rules. ` +
            `To provide extra protocol context, create the file at that path; ` +
            `to silence this notice, set protocolEnforcement.enabled=false.`,
        )
      } catch {
        // Logging is best-effort — never fail the load on a log error.
      }
    }
    return ""
  }
}

// ─── buildSystemInjection ────────────────────────────────────────

/**
 * B1/B2: options.oracleFrequency mirrors scoring.oracleFrequency (default final-only).
 * It only tunes the rule-4 wording; when omitted the legacy gate and text shape
 * are preserved so existing callers and tests keep passing. No default change.
 */
export function buildSystemInjection(
  _protocolText: string,
  options?: { oracleVerified?: boolean; filesChanged?: number; oracleFrequency?: "per-stop" | "final-only" | "off" },
): string {
  const oracleVerified = options?.oracleVerified === true;
  const filesChanged = options?.filesChanged ?? 0;
  // B2: frequency only tunes rule-4 wording; default final-only preserves legacy behavior.
  const oracleFrequency = options?.oracleFrequency ?? "final-only";
  const lines: string[] = [
    "",
    "## Sisyphus Protocol Enforcement",
    "",
    "You MUST follow these Sisyphus protocol rules:",
    "",
    "1. **Pre-response Memory Check**: Before any action, read `<project-memory>` and `<session-history-since>` blocks in context. Cite `<memory id>` for any fact used from memory. Do not ask questions whose answers are in those blocks.",
    "",
    "2. **Codebase Graph First**: Before using grep/glob/read for architecture or symbol queries, check whether `.codegraph/` or `graphify-out/` exists. If so, use codegraph/graphify tools first, then grep/read only as last resort.",
    "",
    // B1: canonical wrapper names (omo_*) — the rest of the plugin (buildProtocolRule,
    // START PROTOCOL, ROUTING_MATRIX) already routes memory through these wrappers.
    // Raw agentmemory_* MCP names are kept only as back-compat in auditToolCall below.
    "3. **Tool Routing Table**: Match common intents to the correct tool:",
    '   - "we did this before" / "you should know" → `omo_recall`',
    "   - Starting a task that resembles a previous one → `omo_recall_mcp`",
    "   - Before asking the user a clarifying question → `omo_recall` first",
    "   - Save durable insight/decision/rule → `omo_remember`",
    "",
  ]

  // v0.29.0: emit rule 4 (Post-task Oracle Verification) only when Oracle
  // hasn't already verified in this session. Once oracleVerified=true the
  // agent has done its job and the rule becomes context-window spam — every
  // subsequent turn re-injects the same "invoke Oracle" reminder.
  // B2(a): truth about the audit side — auditToolCall has NO per-turn Oracle audit.
  // It flags oracle-verification ONLY on IMPLEMENTATION_TOOLS writes once
  // filesChanged >= 3 && !oracleInvoked (write-gated, not every tool call).
  // The system-prompt rule below is the agent's standing instruction, retired
  // once Oracle has been consulted.
  const shouldEmitOracleRule =
    /\boracle\b/i.test(_protocolText) && !(oracleVerified && filesChanged > 0)
  if (shouldEmitOracleRule) {
    lines.push(
      // B2(b): final-only default — the PLUGIN invokes Oracle at the final-gate,
      // so the agent must NOT self-invoke mid-work. Manual invocation is only
      // for per-stop (stop band) or off (manual oracleVerified).
      "4. **Post-task Oracle Verification** (final-gate; frequency=" + oracleFrequency + "): The PLUGIN invokes Oracle automatically at the final-gate (<promise>DONE</promise>/<promise>PLAN-COMPLETE</promise>). Do NOT invoke Oracle mid-work when frequency is final-only (default: zero mid-work invocations, even for stop decisions).",
      "   - Invoke Oracle MANUALLY only when: frequency is per-stop AND the scoring engine reached the stop band (action is stop), OR frequency is off AND you need oracleVerified (for example via omo_recall).",
      '   `task(subagent_type="oracle", run_in_background=false, prompt="Verify: ...")`',
      "   - Final-gate INVOKE triggers: created 1+ new file, modified abstraction, touched security/auth paths, modified DB/persistence, modified CI/CD, added/removed dependency, modified perf-critical path, todo had 2+ completed items.",
      "   - SKIP only when: files touched <= 2, no new file, no dependency change, single-step task, change is typo/comment/rename only.",
      "   - Verdict: PASS → done. FAIL/CONDITIONAL → fix and re-invoke. Max 3 invocations.",
      "   - Cost: Max 3 oracle invocations per task (final-gate only).",
      "",
    )
  }

  lines.push(
    "5. **Parallel Query Rule**: Fire independent tool queries in the same turn. Do NOT serialize independent memory/context queries.",
    "",
    // B1: wrapper route — on empty omo_recall try omo_recall_mcp (cross-session bridge).
    "6. **Empty-Result Escalation**: On empty `omo_recall`, try `omo_recall_mcp` (cross-session bridge) before asking the user.",
    "",
    "7. **Hard Rules (No Exceptions)**:",
    "   - Do NOT ask 'where is X?' if memory or session record already contains it.",
    "   - Do NOT claim 'I don't know' before firing at least 2 different recall tools.",
    "   - Do NOT re-issue clarifying questions whose answer is in `<project-memory>` or `<session-history-since>`.",
    "   - Do NOT suppress type errors with `as any`, `@ts-ignore`, or `@ts-expect-error`.",
    "   - Do NOT leave empty catch blocks `catch(e) {}`.",
    "   - Do NOT start a fresh agent via `task()` when `task(task_id=\"ses_...\")` (continuation) exists — use continuation IDs.",
    "   - Do NOT batch-complete todos — mark each one `completed` immediately after finishing.",
    "   - Do NOT skip creating todos for multi-step tasks.",
    "",
    "8. **Self-Check Before Responding**:",
    "   - Verify you read `<project-memory>` and `<session-history-since>` blocks.",
    "   - Verify you cited `<memory id>` for any fact used from memory.",
    "   - Verify you did not ask a question whose answer is in those blocks.",
    "   - For multi-file changes, verify Oracle was invoked.",
    "   - For codebase exploration, verify codegraph/graphify was tried before grep/read.",
    "   - After discovering something non-obvious, save it with `omo_remember` so future sessions benefit.",
    "   - Do NOT save routine operations (file reads, greps, list commands), trivial decisions, or facts already covered by existing memory. Save only novel insights, non-obvious patterns, or corrections to previous assumptions.",
    "",
    "9. **CI Verification Loop (NON-NEGOTIABLE)**: After EVERY step of an implementation plan that produces a code change you intend to keep, you MUST execute the full CI loop BEFORE moving to the next step:",
    "   a. Commit the changes atomically (one commit per verified increment, never omnibus).",
    "   b. Push to the remote that has CI configured (own repo: `git push origin <branch>`. Fork upstream: `git push fork <branch>` + open/update PR).",
    "   c. Wait for the CI run triggered by the push to reach a terminal state (`gh run watch <run-id> --exit-status`).",
    "   d. Assert `conclusion == \"success\"` on EVERY job. If any job fails, fix the regression, re-push, and re-verify until all jobs are green.",
    "   e. Capture the green run-id so the final plan-completion message can cite it as evidence.",
    "   - Anti-pattern this rule kills: running local tests, seeing 0 failures, declaring done, then shipping the next step on top of an unverified CI. Local green is NOT CI green. Pushed-not-watched is NOT CI green.",
    "   - At the END of the plan (when the last step is done), run a final guarantee: `gh run list --branch <branch> --limit 5` and assert EVERY recent run on this branch has `conclusion=success`. If any run shows `failure`, surface it explicitly to the user with the run-id, the failing job, and the log excerpt - do NOT claim plan success.",
    "   - For fork -> upstream PR workflow: verify the workflow_run on the upstream repo (the PR's head ref), not just the fork. Both must be green.",
    "   - This rule applies whenever the repo has a `.github/workflows/*.yml` file (or equivalent CI config). For repos without CI, document the absence explicitly.",
    "   - If the user explicitly says 'skip CI' or 'local only', this rule is suppressed for that turn ONLY - re-engage on next step.",
    "",
  )

  return lines.join("\n")
}

export interface AuditContext {
  /** Whether any memory tools have been used in this session */
  memoryToolsUsed: readonly string[]
  /** Whether .codegraph/ directory exists in the project */
  hasCodegraphDir: boolean
  /** Whether graphify-out/ directory exists in the project */
  hasGraphifyDir: boolean
  /** Whether Oracle has been invoked in this session */
  oracleInvoked: boolean
  /** Number of files changed so far in this session */
  filesChanged: number
  /** Whether any memory recall tool returned empty results */
  emptyRecall: boolean
  /** Whether any memory smart_search was attempted after empty recall */
  escalationAttempted: boolean
/** Tool names called in this session so far (latest first) */
  recentToolCalls?: readonly string[]
/** File write/edit contents (last 3 for pattern detection) */
  recentWriteContents?: readonly string[]
/** Whether a memory save has been used to persist discoveries */
  memorySaved?: boolean
/** Number of times todo was batch-completed in this session */
  batchCompletions?: number
/**
 * Oracle invocation frequency (mirrors scoring.oracleFrequency / oracle.frequency).
 * v0.56.0 (spam-fix F2): the oracle-verification audit is frequency-aware.
 * With "final-only" (default) the PLUGIN invokes Oracle at the final-gate and
 * mid-work invocation is forbidden — auditing "Oracle was not invoked" on
 * every mid-work write punishes the agent for obeying the system's own rule
 * and produced the Files-changed-(3)(4)(5)(6) lesson spam. "off" likewise
 * never audits. Only "per-stop" audits mid-work (agent must invoke manually
 * at the stop band). Defaults to "final-only" (suppress) when unset so
 * legacy callers without frequency context stop emitting noise.
 */
  oracleFrequency?: "per-stop" | "final-only" | "off"
}

// ─── codebase-graph-first scoping (P2: over-fire fix) ────────────
// The rule targets architecture/symbol queries — broad searches across
// the codebase — NOT directed reads of a known file. A normal exploration
// session does mostly directed reads (exact file paths), which must not
// warn, or the signal drowns (~10 warns/25s) and agents learn to ignore it.

const GLOB_CHARS_RE = /[*?[\]{}]/
const FILE_EXT_RE = /\.[A-Za-z0-9]{1,6}$/

function looksLikeExactFilePath(value: string): boolean {
  const trimmed = value.trim()
  if (trimmed.length === 0) return false
  if (GLOB_CHARS_RE.test(trimmed)) return false
  if (!FILE_EXT_RE.test(trimmed)) return false
  return true
}

function collectStringValues(record: Record<string, unknown>, keys: readonly string[]): string[] {
  const out: string[] = []
  for (const key of keys) {
    const value = record[key]
    if (typeof value === "string" && value.trim().length > 0) {
      out.push(value)
    } else if (Array.isArray(value)) {
      for (const entry of value) {
        if (typeof entry === "string" && entry.trim().length > 0) {
          out.push(entry)
        }
      }
    }
  }
  return out
}

/**
 * Returns true when the grep/glob call is a broad architecture/symbol
 * query (should warn when a graph index exists), false when it is a
 * directed access to a known exact file (exempt — equivalent to a
 * single read, the graph adds no value).
 *
 * - grep with no path (whole-codebase search) → broad.
 * - grep scoped to an exact file path → directed (exempt).
 * - grep scoped to a directory / bare name → broad.
 * - glob with glob chars (`*?[]{}`, e.g. `**\/*.ts`) → broad.
 * - glob with an exact file pattern (`src/foo.ts`) → directed (exempt).
 * - empty/unknown args → broad (preserves pre-fix behaviour).
 */
export function isBroadCodebaseQuery(toolName: string, args: unknown): boolean {
  if (args === null || args === undefined || typeof args !== "object") {
    return true
  }
  const record = args as Record<string, unknown>
  if (Object.keys(record).length === 0) {
    return true
  }
  if (toolName === "grep") {
    const paths = collectStringValues(record, [
      "path",
      "paths",
      "dir",
      "directory",
      "cwd",
      "file",
      "filePath",
      "filename",
    ])
    if (paths.length > 0) {
      if (paths.every((p) => looksLikeExactFilePath(p))) {
        return false
      }
      return true
    }
    return true
  }
  if (toolName === "glob") {
    const patterns = collectStringValues(record, ["pattern", "patterns"])
    if (patterns.length > 0) {
      if (patterns.every((p) => looksLikeExactFilePath(p))) {
        return false
      }
      return true
    }
    const pathValue = record["path"]
    if (typeof pathValue === "string" && looksLikeExactFilePath(pathValue)) {
      return false
    }
    return true
  }
  return true
}

// ─── auditToolCall ────────────────────────────────────────────────

export function auditToolCall(
  toolName: string,
  args: unknown,
  context: AuditContext,
): ProtocolViolation[] {
  const violations: ProtocolViolation[] = []
  const recentToolCalls = context.recentToolCalls ?? []
  const recentWriteContents = context.recentWriteContents ?? []
  const memorySaved = context.memorySaved ?? false
  const batchCompletions = context.batchCompletions ?? 0
  // ── Rule 0.5: Codebase Graph First ──────────────────────────────
  // grep/glob for broad architecture/symbol queries should use
  // codegraph/graphify first. Directed accesses to a known exact file
  // (equivalent to a single read) are exempt — the graph adds no value
  // there and flagging them produced ~10 warns/25s of noise. `read`
  // itself is never flagged (directed by construction).
  if (
    (toolName === "grep" || toolName === "glob") &&
    (context.hasCodegraphDir || context.hasGraphifyDir) &&
    isBroadCodebaseQuery(toolName, args)
  ) {
    const graphType = context.hasCodegraphDir ? ".codegraph" : "graphify-out"
    violations.push({
      rule: "codebase-graph-first",
      tool: toolName,
      severity: "media",
      detail: `Used ${toolName} when ${graphType} exists — should use codegraph/graphify first for architecture and symbol queries.`,
    })
  }

  // ── Rule: Continuation ID discipline ────────────────────────────
  // Starting fresh with task() instead of continuing via task(task_id="ses_...")
  if (toolName === "task" && args && typeof args === "object") {
    const taskArgs = args as Record<string, unknown>
    // If task() has no task_id but has a prompt that resembles a follow-up
    if (!taskArgs.task_id && typeof taskArgs.prompt === "string" && recentToolCalls.length > 3) {
      // Heuristic: if we have more than 3 tool calls in this session and the agent
      // is starting a new task without continuation ID, flag it
      violations.push({
        rule: "continuation-discipline",
        tool: toolName,
        severity: "leve",
        detail:
          "Started a new task() without task(task_id=\"ses_...\") continuation ID. " +
          "Use task_id to preserve the subagent's full context and save tokens.",
      })
    }
  }

  // ── Rule: No type suppression patterns ─────────────────────────
  // v0.38.3 (G1 audit fix): use the canonical IMPLEMENTATION_TOOLS list
  // from skill-priming.ts. Prior to v0.38.3 this was a 5-entry subset that
  // missed multi_edit / apply_patch / ast_grep_replace / refactor, letting
  // those tools bypass the @ts-ignore/as-any audit.
  if (IMPLEMENTATION_TOOLS.includes(toolName)) {
    const content = typeof args === "object" && args !== null
      ? JSON.stringify(args)
      : String(args ?? "")

    const tsIgnore = /@ts-ignore|@ts-expect-error/.test(content)
    const anyCast = /\bas any\b/.test(content)

    if (tsIgnore && anyCast) {
      violations.push({
        rule: "no-type-suppression",
        tool: toolName,
        severity: "grave",
        detail:
          `File write contains both @ts-ignore/@ts-expect-error AND 'as any' — double type suppression. ` +
          `Both are forbidden by the Sisyphus protocol.`,
      })
    } else if (tsIgnore) {
      violations.push({
        rule: "no-type-suppression",
        tool: toolName,
        severity: "media",
        detail:
          `File write contains @ts-ignore or @ts-expect-error — both are forbidden. ` +
          `Fix the underlying type error instead.`,
      })
    } else if (anyCast) {
      violations.push({
        rule: "no-type-suppression",
        tool: toolName,
        severity: "media",
        detail:
          `File write contains 'as any' — type suppression forbidden. ` +
          `Use proper type narrowing instead.`,
      })
    }
  }

  // ── Rule: No empty catch blocks ────────────────────────────────
  // v0.38.3 (G1): see comment above — now uses IMPLEMENTATION_TOOLS.
  if (IMPLEMENTATION_TOOLS.includes(toolName)) {
    const content = typeof args === "object" && args !== null
      ? JSON.stringify(args)
      : String(args ?? "")

    if (/catch\s*\(\s*\w+\s*\)\s*\{\s*\}/.test(content)) {
      violations.push({
        rule: "no-empty-catch",
        tool: toolName,
        severity: "media",
        detail:
          "Empty catch block detected — `catch(e) {}` is forbidden. " +
          "Log the error or handle it gracefully.",
      })
    }
  }

  // ── Rule: Memory discovery not saved ────────────────────────────
  // v0.24.0: narrowed to architecture-level tools only. Routine grep/glob/read
  // are explicitly excluded by the memory save directive ("Do NOT save routine
  // operations"). Only codegraph/graphify queries — which surface non-obvious
  // architectural insights — trigger the save reminder.
  const discoveryTools = ["codegraph_explore", "graphify query"]
  if (discoveryTools.includes(toolName) && recentToolCalls.length >= 2) {
    // Check whether any memory save happened
    if (!memorySaved) {
      violations.push({
        rule: "save-discovery-to-memory",
        tool: toolName,
        severity: "leve",
        detail:
          `Used ${toolName} to discover code but no memory save was made afterwards. ` +
          // B1: wrapper name — raw agentmemory_memory_save is the underlying MCP.
          "Save non-obvious findings with omo_remember so future sessions benefit.",
      })
    }
  }

  // ── Rule: Batch todo completion ─────────────────────────────────
  // Detected by caller passing batchCompletions info
  // The orchestrator/provider sets batchCompletions based on its own monitoring
  if (batchCompletions > 0 && toolName !== "todowrite") {
    violations.push({
      rule: "no-batch-todo-completion",
      tool: toolName,
      severity: "leve",
      detail:
        "Batch todo completion detected. Each todo must be marked `completed` immediately " +
        "after finishing — do not batch-complete multiple items.",
    })
  }

  // ── Rule: Step 3: Empty-result escalation ───────────────────────
  if (context.emptyRecall && !context.escalationAttempted) {
    if (toolName.startsWith("question") || toolName === "ask") {
      violations.push({
        rule: "empty-result-escalation",
        tool: toolName,
        severity: "grave",
        detail:
          "Memory recall returned empty but agent did not try omo_recall_mcp " +
          "before asking the user. Steps 1-3 of empty-result escalation protocol are mandatory.",
      })
    }
  }

  // ── Rule: Memory first before asking questions ──────────────────
  // B1: wrappers first; raw agentmemory_* kept as back-compat so old sessions still count.
  const memoryToolPatterns = [
    "omo_recall", "omo_recall_mcp", "omo_remember",
    "agentmemory_memory_recall", "agentmemory_memory_smart_search",
    "agentmemory_memory_save",
  ]
  const hasUsedMemory = context.memoryToolsUsed.some((t) =>
    memoryToolPatterns.some((p) => t.startsWith(p)),
  )

  if (!hasUsedMemory && (toolName.startsWith("question") || toolName === "ask")) {
    violations.push({
      rule: "memory-first",
      tool: toolName,
      severity: "grave",
      detail:
        "Asked a question without first querying memory. " +
        "Must fire omo_recall or omo_recall_mcp before asking the user.",
    })
  }

  // ── Oracle rule: after multi-file changes ───────────────────────
  // v0.29.0: restrict to WRITE tools only. Previously fired on every tool call
  // once filesChanged>=3 && !oracleInvoked — during background-task waits the
  // agent reads/greps repeatedly, generating the same violation every turn and
  // piling up noise in the context window. The rule is meaningful only when a
  // file has just been touched.
  // v0.38.3 (G1): use IMPLEMENTATION_TOOLS — same set as plugin.ts filesChanged.
  // v0.56.0 (spam-fix F2): frequency-aware. With oracle.frequency="final-only"
  // (default) or "off", mid-work Oracle invocation is suppressed BY DESIGN
  // (selectEscalationTarget returns null; the final-gate invokes Oracle), so
  // auditing "Oracle was not invoked" here punishes obedience and generated
  // the Files-changed-(3)(4)(5)(6) AgentMemory spam class. Only "per-stop"
  // audits mid-work, where the agent must invoke Oracle manually at stop band.
  const oracleFrequency = context.oracleFrequency ?? "final-only"
  if (
    oracleFrequency === "per-stop" &&
    context.filesChanged >= 3 &&
    !context.oracleInvoked &&
    IMPLEMENTATION_TOOLS.includes(toolName)
  ) {
    violations.push({
      rule: "oracle-verification",
      tool: toolName,
      severity: "media",
      detail:
        `Files changed (${context.filesChanged}) >= 3 but Oracle was not invoked. ` +
        "Oracle review is required at the stop band (per-stop frequency) or the final-gate.",
    })
  }

  return violations
}
